import { ApiError } from '../api/client';
import { CHANGED_MEANWHILE, LIVE_UPDATED, SAVED_BUT_OFFLINE, publishLatest, saveSource } from './livePage';
import { useProjects } from './projects';

/**
 * A Manage edit — a row in the page's content, a phrase of its wording — saved and put on the
 * live page, by the SAME two steps a chat edit takes (state/livePage.ts): `saveSource` onto the
 * record as stored, then `publishLatest` as a CONTENT update. So a page its owner took offline
 * stays offline and a password page keeps its password, and a failed publish costs a page that
 * is briefly behind — which Publish changes then offers to put right — never the edit.
 *
 * Rows held on the server (the Live section) do not come through here: they change nothing in
 * the page's source, so there is nothing to republish.
 */

/**
 * Said when the stored app moved on after this screen drew it. The phone's words for its Manage
 * tab (src/store/webEditPublish.ts); chat's own sentence says "send your request again", which
 * is not what a person editing a row did.
 */
export const MANAGE_CHANGED_MEANWHILE =
  'Your app was changed while this edit was being made, so nothing was overwritten. Make your change again on the current version.';

/** Chat's words for a publish that got no answer (src/webapp/chatEdit.ts), so the two agree. */
const PUBLISH_AGAIN = 'Your change is saved — publish again when you have a connection.';

export type ManageSaved =
  /** On the page people open. */
  | { status: 'live'; message: string }
  /** Saved and on the page, but the page is switched off. */
  | { status: 'offline'; message: string }
  /** Saved; the page is behind, and says so until Publish changes puts it right. */
  | { status: 'saved_not_live'; message: string };

/**
 * Why the page was not updated, as chat says it (ChatPanel's `publishFailure` with chatEdit's
 * sentences): only a request that got no answer is a connection problem. A takedown, a page over
 * the size limit, an expired session were DECIDED, and "when you have a connection" would send
 * somebody to check a connection that was fine.
 */
function notLive(err: unknown): string {
  if (err instanceof ApiError && (err.kind === 'offline' || err.kind === 'timeout')) {
    return `${err.message} ${PUBLISH_AGAIN}`;
  }
  const sentence =
    err instanceof ApiError && (err.code === 'taken_down' || err.takenDown === true)
      ? err.reason?.trim() || err.message
      : err instanceof Error
        ? err.message.trim()
        : '';
  return `Saved, but not live: ${sentence || 'the server would not publish this page.'}`;
}

/**
 * Save `next` over `sent` — the source the edit was made FROM — and publish it.
 *
 * `what` is the change in words ("Change an item in COURSES"). It is kept with the version this
 * replaces, as a chat edit keeps its instruction, so Undo last change in Chat can take a Manage
 * edit back and say which one it took back.
 *
 * Throws only when nothing was saved, so the editor stays open with what was typed. Anything
 * after the save resolves: the change is kept either way, and the outcome says where it went.
 */
export async function saveManagedSource(
  projectId: string,
  ownerId: string,
  sent: string,
  next: string,
  what = '',
): Promise<ManageSaved> {
  try {
    await saveSource(projectId, ownerId, sent, next, what);
  } catch (err) {
    // Show the version that is stored now: it is the one the owner's next change is made to.
    void useProjects.getState().loadProject(projectId, ownerId);
    if (err instanceof Error && err.message === CHANGED_MEANWHILE) throw new Error(MANAGE_CHANGED_MEANWHILE);
    throw err;
  }
  try {
    const published = await publishLatest(projectId, ownerId);
    return published.webPublished
      ? { status: 'live', message: LIVE_UPDATED }
      : { status: 'offline', message: SAVED_BUT_OFFLINE };
  } catch (err) {
    return { status: 'saved_not_live', message: notLive(err) };
  }
}
